import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import type { PermissionScope } from './usePermission';

/** Экран «Роли и права»: ответы `/roles/*` бэкенда. */
export interface Grant {
  permission: string;
  scope: PermissionScope;
}

export interface CatalogPermission {
  key: string;
  module: string;
  scopes: PermissionScope[];
  isSensitive: boolean;
  derivedFrom: string | null;
  labelKey: string;
}

export type ComputedRoleKey = 'employee' | 'manager' | 'managerOfManagers';

export interface Catalog {
  modules: { key: string; labelKey: string }[];
  permissions: CatalogPermission[];
  computedRoles: Record<ComputedRoleKey, Grant[]>;
}

export interface Role {
  _id: string;
  key: string;
  name: string;
  description?: string;
  /** `'*'` — «Все права», включая будущие, кроме `except`. */
  grants: Grant[] | '*';
  except: string[];
  isAll: boolean;
  isTemplate: boolean;
  isCustomized: boolean;
  holders: number;
}

export interface Person {
  _id: string;
  name: string;
  email?: string;
  active?: boolean;
}

export interface AssignmentRow {
  _id: string;
  // Роли из переключателей карточки — тоже выдачи (перевод флагов, шаг 4 спеки прав).
  source: 'assignment';
  user: Person;
  role: { _id: string | null; key: string; name: string };
  grantedBy?: { _id: string; name: string } | null;
  grantedAt?: string;
  expiresAt?: string | null;
}

export type Reason = { kind: 'teamLead' | 'teamPm' | 'departmentHead'; id: string; name: string };

export type ExplainSource =
  | { kind: 'owner'; scope: PermissionScope }
  | {
      kind: 'role';
      key: string;
      name: string;
      scope: PermissionScope;
      via: ('flag' | 'assignment')[];
    }
  | { kind: 'computed'; key: ComputedRoleKey; scope: PermissionScope; reasons?: Reason[] };

export interface Explain {
  user: { _id: string; name: string };
  permission: string;
  isOwner: boolean;
  sources: ExplainSource[];
}

export interface AuditItem {
  _id: string;
  action: string;
  createdAt: string;
  actor: { _id: string; name: string } | null;
  subject: { _id: string; name: string } | null;
  details: Record<string, unknown>;
  ip: string | null;
}

export interface AuditFilter {
  action?: string;
  person?: string;
  from?: string;
  to?: string;
  page: number;
}

const qs = (o: Record<string, string | number | undefined>) =>
  new URLSearchParams(
    Object.entries(o)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => [k, String(v)]),
  ).toString();

export const useRolesCatalog = () =>
  useQuery({
    queryKey: ['roles', 'catalog'],
    queryFn: (): Promise<Catalog> => api('/roles/catalog'),
    staleTime: Infinity,
  });

export const useRoles = () =>
  useQuery({ queryKey: ['roles', 'list'], queryFn: (): Promise<Role[]> => api('/roles') });

export const useRoleAssignments = () =>
  useQuery({
    queryKey: ['roles', 'assignments'],
    queryFn: (): Promise<{ ownerId: string | null; items: AssignmentRow[] }> =>
      api('/roles/assignments'),
  });

export const useExplain = (user: string | null, permission: string | null) =>
  useQuery({
    queryKey: ['roles', 'explain', user, permission],
    queryFn: (): Promise<Explain> => api(`/roles/explain?${qs({ user, permission } as never)}`),
    enabled: !!user && !!permission,
  });

export const useAudit = (filter: AuditFilter) =>
  useQuery({
    queryKey: ['roles', 'audit', filter],
    queryFn: (): Promise<{ items: AuditItem[]; total: number; page: number; limit: number }> =>
      api(
        `/roles/audit?${qs({
          ...filter,
          // Даты — границы дня в поясе браузера: «по 1 февраля» включает весь этот день.
          from: filter.from && new Date(`${filter.from}T00:00:00`).toISOString(),
          to: filter.to && new Date(`${filter.to}T23:59:59.999`).toISOString(),
          limit: 50,
        })}`,
      ),
    placeholderData: (prev) => prev,
  });

/** Любая правка ролей меняет и список, и выдачи, и права — сбрасываем всё `roles` и свои права. */
const useRolesMutation = <T, R = unknown>(fn: (vars: T) => Promise<R>) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['roles'] });
      client.invalidateQueries({ queryKey: ['users', 'me', 'permissions'] });
    },
  });
};

type RoleBody = { name: string; description?: string; grants: Grant[] };

export const useSaveRole = () =>
  useRolesMutation(
    ({ id, ...body }: RoleBody & { id?: string }): Promise<Role> =>
      id ? api(`/roles/${id}`, { method: 'PATCH', body }) : api('/roles', { method: 'POST', body }),
  );

export const useDeleteRole = () =>
  useRolesMutation((id: string) => api(`/roles/${id}`, { method: 'DELETE' }));

export const useGrantRole = () =>
  useRolesMutation((body: { user: string; role: string }) =>
    api('/roles/assignments', { method: 'POST', body }),
  );

export const useRevokeRole = () =>
  useRolesMutation((id: string) => api(`/roles/assignments/${id}`, { method: 'DELETE' }));
